// Geometry of the splash-page orbits, expressed in the 1200x600 coordinate space of
// SplashScreenCircles. Tiles used to be placed with `offset-path: url(#ellipse)`, but
// browsers disagree on where an SVG ellipse path starts, which way it runs and how its
// length is measured, so the same percentages produced different layouts. Positions are
// now computed here and applied as plain left/top values, which every browser renders alike.
export const ORBIT_BASE_WIDTH = 1200;
export const ORBIT_BASE_HEIGHT = 600;
export const TILE_SIZE = 100;
// Distance from the orbit centre to the JESA logo centre, in unscaled wrapper units.
// Matches the historical `top: 30%` of a 1080px-tall viewport, but follows the ring
// when the scale is driven by a narrow viewport instead of the height.
export const LOGO_OFFSET_ABOVE_CENTER = 131;

// Same values as the <ellipse> elements in SplashScreenCircles (cx/cy/rx/ry percentages).
export const ORBITS = {
  small: { cx: 0.5, cy: 0.26, rx: 0.162, ry: 0.1 },
  medium: { cx: 0.5, cy: 0.35, rx: 0.3, ry: 0.195 },
  big: { cx: 0.5, cy: 0.45, rx: 0.437, ry: 0.301 },
};

const SEGMENTS = 3600;
const lengthTables = {};

// Cumulative arc length of the ellipse, starting at the 3 o'clock point and running
// clockwise on screen (the SVG path convention the original design was tuned against).
const getLengthTable = (orbit) => {
  const key = `${orbit.rx}-${orbit.ry}`;
  if (lengthTables[key]) return lengthTables[key];
  const rx = orbit.rx * ORBIT_BASE_WIDTH;
  const ry = orbit.ry * ORBIT_BASE_HEIGHT;
  const table = [0];
  let prevX = rx;
  let prevY = 0;
  for (let i = 1; i <= SEGMENTS; i += 1) {
    const angle = (i / SEGMENTS) * 2 * Math.PI;
    const x = rx * Math.cos(angle);
    const y = ry * Math.sin(angle);
    table.push(table[i - 1] + Math.hypot(x - prevX, y - prevY));
    prevX = x;
    prevY = y;
  }
  lengthTables[key] = table;
  return table;
};

/**
 * Point on an orbit at a given distance, as a percentage of its perimeter.
 * Returns pixel coordinates in the unscaled 1200x600 wrapper.
 */
export const getOrbitPoint = (orbitName, distancePercent) => {
  const orbit = ORBITS[orbitName];
  const table = getLengthTable(orbit);
  const perimeter = table[SEGMENTS];
  const target = ((((distancePercent % 100) + 100) % 100) / 100) * perimeter;
  let index = 0;
  while (index < SEGMENTS && table[index + 1] < target) index += 1;
  const segmentLength = table[index + 1] - table[index];
  const fraction = segmentLength > 0 ? (target - table[index]) / segmentLength : 0;
  const angle = ((index + fraction) / SEGMENTS) * 2 * Math.PI;
  return {
    x: (orbit.cx + orbit.rx * Math.cos(angle)) * ORBIT_BASE_WIDTH,
    y: orbit.cy * ORBIT_BASE_HEIGHT + orbit.ry * ORBIT_BASE_HEIGHT * Math.sin(angle),
  };
};

/** CSS left/top for a tile centred on the orbit point. */
export const getOrbitTileStyle = (orbitName, distancePercent) => {
  const { x, y } = getOrbitPoint(orbitName, distancePercent);
  return { left: `${x.toFixed(2)}px`, top: `${y.toFixed(2)}px` };
};

/** Spacing of the main rings: first tile distance and gap between tiles, in percent. */
export const PLATFORM_RINGS = {
  small: { start: 16.5, gap: 17, count: 2 },
  medium: { start: 12, gap: 6.375, count: 5 },
  big: { start: 9.75, gap: 4.25, count: 8 },
};

export const SUB_PLATFORM_POSITIONS = {
  small: { subPlatformSE1: 16.5, subPlatformSE2: 33.5 },
  medium: {
    subPlatformME1: 14.5,
    subPlatformME2: 25,
    subPlatformME3: 35.5,
    subPlatformME4: 46,
  },
};
